/**
 * The page's half of `ViewRegistry` (docs/ops-protocol.md, "Registered
 * views"): a view a package draws itself, by name, which the `registered`
 * element kind names. A native app registers a SwiftUI view under the same
 * name (SwiftBrowserNative's `ViewRegistry`), so one component
 * (`RegisteredView` in @swiftbrowser/react) is a SwiftUI view in the app and
 * the package's own DOM on a page, drawing what never crosses the bridge
 * (an audio node's frames, a map's tiles).
 *
 * ```ts
 * ViewRegistry.register('Meter', (context) => {
 *   const element = document.createElement('canvas');
 *   return { element, update: (props) => …, dispose: () => … };
 * });
 * ```
 */

/** What a builder gets: the element's id, its props as the app wrote them, and a way to answer (`onEvent`). */
export interface RegisteredViewContext {
  readonly id: number;
  readonly props: Record<string, unknown>;
  /** Sends a value to the element's `onEvent` in the app. */
  send(value: unknown): void;
}

/** A built view: its element, which fills the box the layout gives it, and what to do as props change or it goes. */
export interface RegisteredViewInstance {
  readonly element: HTMLElement;
  update?(props: Record<string, unknown>): void;
  dispose?(): void;
}

export type RegisteredViewBuilder = (context: RegisteredViewContext) => RegisteredViewInstance | HTMLElement;

const builders = new Map<string, RegisteredViewBuilder>();

export const ViewRegistry = {
  /** Registers `builder` under `name`, replacing one registered before; elements made after use it. */
  register(name: string, builder: RegisteredViewBuilder): void {
    builders.set(name, builder);
  },

  unregister(name: string): void {
    builders.delete(name);
  },

  isRegistered(name: string): boolean {
    return builders.has(name);
  },

  /** The view registered under `name`, built; undefined when there is none. */
  make(name: string, context: RegisteredViewContext): RegisteredViewInstance | undefined {
    const builder = builders.get(name);
    if (!builder) return undefined;
    const made = builder(context);
    return made instanceof HTMLElement ? { element: made } : made;
  },
};
